/**
 * 财务管理服务 - 财务相关 API
 * 路径与后端 /fin/* 对齐
 */

import api from '../../api';
import type { Result, PageResult } from '../../types';

// ==================== 合同管理 ====================

export const getContracts = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/contracts', { params });
};

export const getContractById = (id: string): Promise<Result<Record<string, unknown>>> => {
  return api.get(`/fin/contracts/${id}`);
};

export const createContract = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/contracts', data);
};

export const updateContract = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/contracts/${id}`, data);
};

export const deleteContract = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/contracts/${id}`);
};

export const getContractStats = (): Promise<Result<Record<string, unknown>>> => {
  return api.get('/fin/contracts/stats');
};

// ==================== 应收款管理 ====================

export const getReceivablePlans = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/receivables', { params });
};

export const getReceivableById = (id: string): Promise<Result<Record<string, unknown>>> => {
  return api.get(`/fin/receivables/${id}`);
};

export const createReceivable = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/receivables', data);
};

export const updateReceivable = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/receivables/${id}`, data);
};

export const deleteReceivable = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/receivables/${id}`);
};

/** 别名：与应收创建/更新/删除同源，兼容旧命名 */
export const createReceivablePlan = createReceivable;
export const updateReceivablePlan = updateReceivable;
export const deleteReceivablePlan = deleteReceivable;

export const getReceivablesByContractId = (contractId: string): Promise<Result<Record<string, unknown>[]>> => {
  return api.get(`/fin/receivables/contract/${contractId}`);
};

export const getOverdueReceivables = (): Promise<Result<Record<string, unknown>[]>> => {
  return api.get('/fin/receivables/overdue');
};

export const getDueSoonReceivables = (days: number = 7): Promise<Result<Record<string, unknown>[]>> => {
  return api.get('/fin/receivables/due-soon', { params: { days } });
};

export const confirmReceivablePayment = (id: string, amount: number): Promise<Result<void>> => {
  return api.put(`/fin/receivables/${id}/payment`, null, { params: { amount } });
};

// ==================== 回款记录 ====================

export const getPaymentRecords = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/payment-records', { params });
};

export const createPaymentRecord = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/payment-records', data);
};

export const updatePaymentRecord = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/payment-records/${id}`, data);
};

export const deletePaymentRecord = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/payment-records/${id}`);
};

// ==================== 发票管理 ====================

export const getInvoices = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/invoices', { params });
};

export const getInvoiceById = (id: string): Promise<Result<Record<string, unknown>>> => {
  return api.get(`/fin/invoices/${id}`);
};

export const createInvoice = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/invoices', data);
};

export const updateInvoice = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/invoices/${id}`, data);
};

export const deleteInvoice = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/invoices/${id}`);
};

export const voidInvoice = (id: string, reason: string, version: number): Promise<Result<void>> => {
  return api.put(`/fin/invoices/${id}/void`, { reason, version });
};

export interface InvoiceIssuePayload {
  version: number;
  invoiceNo: string;
  issuedDate: string;
  files: string;
  remark?: string;
}

export const issueInvoice = (id: string, data: InvoiceIssuePayload): Promise<Result<void>> => {
  return api.put(`/fin/invoices/${id}/issue`, data);
};

export const claimInvoice = (id: string, version: number): Promise<Result<void>> => {
  return api.put(`/fin/invoices/${id}/claim`, { version });
};

export const uploadInvoiceFile = (file: File): Promise<Result<{ url: string }>> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', 'invoices');
  return api.post('/system/files/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// ==================== 费用报销 ====================

export const getExpenses = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/expenses', { params });
};

export const getExpenseById = (id: string): Promise<Result<Record<string, unknown>>> => {
  return api.get(`/fin/expenses/${id}`);
};

export const createExpense = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/expenses', data);
};

export const updateExpense = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/expenses/${id}`, data);
};

export const deleteExpense = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/expenses/${id}`);
};

/** 提交报销审批（启动工作流） */
export const submitExpense = (id: string, version: number): Promise<Result<void>> => {
  return api.post(`/fin/expenses/${id}/submit`, { version });
};

/** 标记报销已付款（后端要求当前状态为已通过审批） */
export const approveExpense = (id: string, comment?: string): Promise<Result<void>> => {
  return api.post(`/fin/expenses/${id}/approve`, { status: 'approved', comment });
};

export const rejectExpense = (id: string, comment: string): Promise<Result<void>> => {
  return api.post(`/fin/expenses/${id}/reject`, { status: 'rejected', comment });
};

export const payExpense = (id: string, data?: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/expenses/${id}/pay`, data ?? {});
};

// ==================== 借款管理 ====================

export const getLoans = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/loans', { params });
};

export const getLoanById = (id: string): Promise<Result<Record<string, unknown>>> => {
  return api.get(`/fin/loans/${id}`);
};

export const createLoan = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/loans', data);
};

export const updateLoan = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/loans/${id}`, data);
};

export const deleteLoan = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/loans/${id}`);
};

// ==================== 应付款管理 ====================

export const getPayables = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/payables', { params });
};

export const getPayableById = (id: string): Promise<Result<Record<string, unknown>>> => {
  return api.get(`/fin/payables/${id}`);
};

export const createPayable = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/payables', data);
};

export const updatePayable = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/payables/${id}`, data);
};

export const deletePayable = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/payables/${id}`);
};

export const confirmPayment = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/payables/${id}/pay`, data);
};

// ==================== 供应商管理（财务主数据 fin_supplier，路径 /fin/suppliers） ====================

export const getSuppliers = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/suppliers', { params });
};

export const getSupplierById = (id: string): Promise<Result<Record<string, unknown>>> => {
  return api.get(`/fin/suppliers/${id}`);
};

export const createSupplier = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/suppliers', data);
};

export const updateSupplier = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/suppliers/${id}`, data);
};

export const deleteSupplier = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/suppliers/${id}`);
};

// ==================== 月结费用（按月经销聚合，门户统计用） ====================

export const getMonthlyExpenses = (params: Record<string, unknown>): Promise<Result<Record<string, unknown>[]>> => {
  return api.get('/fin/monthly-expenses', { params });
};

// ==================== 费用分摊台账（fin_monthly_expense） ====================

export const getMonthlyExpenseLedger = (params?: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/monthly-expense-ledger', { params, enableCache: false });
};

export const getMonthlyExpenseById = (id: string): Promise<Result<Record<string, unknown>>> => {
  return api.get(`/fin/monthly-expense-ledger/${id}`);
};

export const createMonthlyExpense = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/monthly-expense-ledger', data);
};

export const updateMonthlyExpense = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/monthly-expense-ledger/${id}`, data);
};

export const deleteMonthlyExpense = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/monthly-expense-ledger/${id}`);
};

// ==================== 资金流水 ====================

export const getFundRecords = (params: Record<string, unknown>): Promise<Result<PageResult<Record<string, unknown>>>> => {
  return api.get('/fin/fund-records', { params });
};

export const createFundRecord = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/fin/fund-records', data);
};

export const updateFundRecord = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/fin/fund-records/${id}`, data);
};

export const deleteFundRecord = (id: string): Promise<Result<void>> => {
  return api.delete(`/fin/fund-records/${id}`);
};

// ==================== 财务配置 ====================

export const getFinanceConfig = (): Promise<Result<Record<string, unknown>>> => {
  return api.get('/fin/finance-config');
};

export const updateFinanceConfig = (data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put('/fin/finance-config', data);
};

// ==================== 财务报表 ====================

export const getFinanceStats = (): Promise<Result<Record<string, unknown>>> => {
  return api.get('/fin/stats');
};

export const getIncomeStats = (): Promise<Result<Record<string, unknown>>> => {
  return api.get('/fin/income-stats');
};

export const getExpenseStats = (): Promise<Result<Record<string, unknown>>> => {
  return api.get('/fin/expense-stats');
};

// ==================== 催收记录（应收） ====================
// 已移除：业务定位为业务财务，逾期只做提醒，不做催收台账。
